import { BaseIntegration } from '../../core/BaseIntegration';
import type { IntegrationConfig, IntegrationResult, IntegrationError, IntegrationParams } from '../../types';
import { registerIntegration } from '../../registry';

/** Shape of a stored object in the in-memory store */
interface StoredObject {
  content: unknown;
  contentType: string;
  size: number;
  metadata: IntegrationParams;
  lastModified: number;
  etag: string;
}

/**
 * Storage integration with in-memory backend for development/testing.
 *
 * Provides S3-compatible object storage operations (upload, download, list,
 * delete, getSignedUrl) backed by an in-memory Map. No cloud SDK dependencies
 * are required.
 */
export class StorageIntegration extends BaseIntegration {
  private objects: Map<string, StoredObject> = new Map();

  constructor(config: IntegrationConfig) {
    super(config);

    const storageUrl = config.env.STORAGE_URL;
    if (storageUrl) {
      this.logger.warn(
        'STORAGE_URL is configured but real storage client is not yet implemented. ' +
          'Falling back to in-memory store.',
        { storageUrl },
      );
    }

    this.logger.info('Storage integration initialized (in-memory backend)');
  }

  async execute(
    action: string,
    params: IntegrationParams,
  ): Promise<IntegrationResult> {
    const validation = this.validateParams(action, params);
    if (!validation.valid) {
      return {
        success: false,
        error: {
          name: 'IntegrationError',
          message: 'Validation failed',
          code: 'VALIDATION_ERROR',
          details: validation.errors,
        } as IntegrationError,
        metadata: this.createMetadata(action, 0),
      };
    }

    const startTime = Date.now();

    try {
      let data: unknown;

      switch (action) {
        case 'upload':
          data = await this.executeWithRetry(() => this.upload(params));
          break;
        case 'download':
          data = await this.executeWithRetry(() => this.download(params));
          break;
        case 'list':
          data = await this.executeWithRetry(() => this.list(params));
          break;
        case 'delete':
          data = await this.executeWithRetry(() => this.deleteObject(params));
          break;
        case 'getSignedUrl':
          data = await this.executeWithRetry(() => this.getSignedUrl(params));
          break;
        default:
          throw new Error(`Unknown action: ${action}`);
      }

      return {
        success: true,
        data,
        metadata: this.createMetadata(action, Date.now() - startTime),
      };
    } catch (error) {
      return this.handleError(action, error);
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /** Build a composite key from bucket and object key. */
  private compositeKey(bucket: string, key: string): string {
    return `${bucket}/${key}`;
  }

  /** Generate a deterministic etag from content. */
  private generateEtag(content: unknown): string {
    const raw = typeof content === 'string' ? content : JSON.stringify(content);
    // Simple hash — good enough for in-memory dev usage
    let hash = 0;
    for (let i = 0; i < raw.length; i++) {
      const ch = raw.charCodeAt(i);
      hash = ((hash << 5) - hash + ch) | 0;
    }
    return `"${Math.abs(hash).toString(16).padStart(8, '0')}"`;
  }

  /** Compute the byte size of content. */
  private computeSize(content: unknown): number {
    if (typeof content === 'string') {
      return new TextEncoder().encode(content).byteLength;
    }
    return JSON.stringify(content).length;
  }

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  private async upload(
    params: IntegrationParams,
  ): Promise<{ key: string; bucket: string; size: number; etag: string }> {
    const bucket = params.bucket as string;
    const key = params.key as string;
    const content = params.content;
    const contentType = (params.contentType as string | undefined) ?? 'application/octet-stream';
    const metadata = (params.metadata as IntegrationParams | undefined) ?? {};

    this.logger.debug('Storage UPLOAD', { bucket, key, contentType });

    const size = this.computeSize(content);
    const etag = this.generateEtag(content);

    const obj: StoredObject = {
      content,
      contentType,
      size,
      metadata,
      lastModified: Date.now(),
      etag,
    };

    this.objects.set(this.compositeKey(bucket, key), obj);

    return { key, bucket, size, etag };
  }

  private async download(
    params: IntegrationParams,
  ): Promise<{
    content: unknown;
    contentType: string;
    size: number;
    metadata: IntegrationParams;
  }> {
    const bucket = params.bucket as string;
    const key = params.key as string;

    this.logger.debug('Storage DOWNLOAD', { bucket, key });

    const obj = this.objects.get(this.compositeKey(bucket, key));
    if (!obj) {
      throw new Error(`Object not found: ${bucket}/${key}`);
    }

    return {
      content: obj.content,
      contentType: obj.contentType,
      size: obj.size,
      metadata: obj.metadata,
    };
  }

  private async list(
    params: IntegrationParams,
  ): Promise<{
    keys: Array<{ key: string; size: number; lastModified: number }>;
    truncated: boolean;
  }> {
    const bucket = params.bucket as string;
    const prefix = (params.prefix as string | undefined) ?? '';
    const maxKeys = (params.maxKeys as number | undefined) ?? 1000;

    this.logger.debug('Storage LIST', { bucket, prefix, maxKeys });

    const bucketPrefix = `${bucket}/`;
    const fullPrefix = `${bucket}/${prefix}`;
    const results: Array<{ key: string; size: number; lastModified: number }> = [];

    for (const [compositeKey, obj] of this.objects) {
      if (!compositeKey.startsWith(fullPrefix)) continue;

      // Strip the bucket prefix to get the object key
      const objectKey = compositeKey.slice(bucketPrefix.length);
      results.push({
        key: objectKey,
        size: obj.size,
        lastModified: obj.lastModified,
      });
    }

    // Sort by key for deterministic output
    results.sort((a, b) => a.key.localeCompare(b.key));

    const truncated = results.length > maxKeys;
    return {
      keys: results.slice(0, maxKeys),
      truncated,
    };
  }

  private async deleteObject(
    params: IntegrationParams,
  ): Promise<{ deleted: boolean }> {
    const bucket = params.bucket as string;
    const key = params.key as string;

    this.logger.debug('Storage DELETE', { bucket, key });

    const existed = this.objects.has(this.compositeKey(bucket, key));
    this.objects.delete(this.compositeKey(bucket, key));
    return { deleted: existed };
  }

  private async getSignedUrl(
    params: IntegrationParams,
  ): Promise<{ url: string; expiresAt: number }> {
    const bucket = params.bucket as string;
    const key = params.key as string;
    const expiresIn = (params.expiresIn as number | undefined) ?? 3600;
    const operation = (params.operation as 'get' | 'put' | undefined) ?? 'get';

    this.logger.debug('Storage GET_SIGNED_URL', { bucket, key, expiresIn, operation });

    const expiresAt = Date.now() + expiresIn * 1000;
    const token = Math.random().toString(36).slice(2, 18);
    const url =
      `https://storage.mock.local/${bucket}/${key}` +
      `?X-Amz-Algorithm=MOCK-HMAC-SHA256` +
      `&X-Amz-Expires=${expiresIn}` +
      `&X-Amz-SignedHeaders=host` +
      `&X-Amz-Signature=${token}` +
      `&operation=${operation}`;

    return { url, expiresAt };
  }
}

// Auto-register
registerIntegration('storage', StorageIntegration);
